import type {
  CustomerCreate,
  CustomerRename,
  CustomerTeamAssignmentCreate,
  CustomerTeamAssignmentRemove,
  KnownCustomerNumber,
} from '@solidops/shared/customers';

import {
  createCustomer as createCustomerRequest,
  getGetCustomerQueryKey,
  getGetCustomerQueryOptions,
  getListCustomersQueryKey,
  getListCustomersQueryOptions,
  renameCustomer as renameCustomerRequest,
} from './generated/customers/customers.js';
import {
  addCustomerTeamAssignment as addAssignmentRequest,
  getListCustomerTeamAssignmentsQueryKey,
  getListCustomerTeamAssignmentsQueryOptions,
  removeCustomerTeamAssignment as removeAssignmentRequest,
} from './generated/customer-team-assignments/customer-team-assignments.js';
import {
  addCustomerKnownNumber as addKnownNumberRequest,
  editCustomerKnownNumber as editKnownNumberRequest,
  getListCustomerKnownNumbersQueryKey,
  getListCustomerKnownNumbersQueryOptions,
  removeCustomerKnownNumber as removeKnownNumberRequest,
} from './generated/customer-known-numbers/customer-known-numbers.js';

type CommandHeaders = { csrfToken: string; idempotencyKey: string };

function headers(input: CommandHeaders) {
  return { 'x-csrf-token': input.csrfToken, 'idempotency-key': input.idempotencyKey };
}

export const getCustomerQueryKey = getGetCustomerQueryKey;
export const getCustomerQueryOptions = getGetCustomerQueryOptions;
export const getCustomersQueryKey = getListCustomersQueryKey;
export const getCustomerAssignmentsQueryKey = getListCustomerTeamAssignmentsQueryKey;
export const getCustomerAssignmentsQueryOptions = getListCustomerTeamAssignmentsQueryOptions;
export const getCustomerKnownNumbersQueryKey = getListCustomerKnownNumbersQueryKey;
export const getCustomerKnownNumbersQueryOptions = getListCustomerKnownNumbersQueryOptions;

export function getCustomersQueryOptions(input: {
  query?: string;
  status: 'active' | 'archived';
  cursor?: string;
}) {
  return getListCustomersQueryOptions({ ...input, limit: 20 });
}

export function createCustomer(input: CustomerCreate & CommandHeaders) {
  return createCustomerRequest({ name: input.name }, { headers: headers(input) });
}

export function renameCustomer(customerId: string, input: CustomerRename & CommandHeaders) {
  return renameCustomerRequest(
    customerId,
    {
      name: input.name,
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}

export function addCustomerAssignment(
  customerId: string,
  input: CustomerTeamAssignmentCreate & CommandHeaders,
) {
  return addAssignmentRequest(
    customerId,
    {
      teamId: input.teamId,
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}

export function removeCustomerAssignment(
  customerId: string,
  assignmentId: string,
  input: CustomerTeamAssignmentRemove & CommandHeaders,
) {
  return removeAssignmentRequest(
    customerId,
    assignmentId,
    {
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}

export function addCustomerKnownNumber(
  customerId: string,
  input: { number: string; expectedCustomerVersion: number } & CommandHeaders,
) {
  return addKnownNumberRequest(
    customerId,
    {
      number: input.number,
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}

export function editCustomerKnownNumber(
  customerId: string,
  knownNumberId: KnownCustomerNumber['id'],
  input: { number: string; expectedCustomerVersion: number } & CommandHeaders,
) {
  return editKnownNumberRequest(
    customerId,
    knownNumberId,
    {
      number: input.number,
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}

export function removeCustomerKnownNumber(
  customerId: string,
  knownNumberId: KnownCustomerNumber['id'],
  input: { expectedCustomerVersion: number } & CommandHeaders,
) {
  return removeKnownNumberRequest(
    customerId,
    knownNumberId,
    {
      expectedCustomerVersion: input.expectedCustomerVersion,
    },
    { headers: headers(input) },
  );
}
